// src/pages/ApiDoc.js

import React, { useState, useMemo } from "react";
import {
  Box, Typography, Container, Grid, List, ListItemButton, ListItemText, Chip, IconButton, Button, Card, CardContent, AppBar, Toolbar, Avatar, Stack, Badge,
} from "@mui/material";
import {
  Book, Zap, Code, Shield, Rocket, Globe, Server, Activity, Lock, ExternalLink, Database, Cpu,
  Check,
  Copy
} from "lucide-react";

// --- IMPORT MODULAR DATA AND COMPONENT ---
import ApiSection from "./ApiSection"; 
import { apiMap, SIM_INVENTORY_KEY, methodConfig, statusCodes, getHeadersFromEndpoints } from "./apiData";

const ApiDoc = () => {
  // New state to manage which API's documentation is currently visible
  const [activeApiKey, setActiveApiKey] = useState(SIM_INVENTORY_KEY);
  const [activeEndpoint, setActiveEndpoint] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  // Dynamic content based on the activeApiKey
  const currentApiData = useMemo(() => apiMap[activeApiKey], [activeApiKey]);
  const apiEndpoints = currentApiData.endpoints;
  const requiredHeaders = useMemo(() => getHeadersFromEndpoints(apiEndpoints), [apiEndpoints]);

  const handleApiSwitch = (key) => {
    setActiveApiKey(key);
    setActiveEndpoint(null); // Reset active endpoint when switching APIs
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(type);
    setTimeout(() => setCopiedCode(null), 2000);
  };
  
  return (
    <Box sx={{ bgcolor: "#fafafa", minHeight: "100vh", mt: 8 }}>
      {/* Dynamic Header */}
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{ 
          bgcolor: "var(--bg)",
          background: "var(--bg)"
        }}
      >
        <Toolbar sx={{ py: 2 }}>
          <Avatar sx={{ bgcolor: "#fff", color: "#667eea", mr: 2 }}>
            {/* Display icon of the current API */}
            {currentApiData.icon} 
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5" fontWeight="bold">
              {currentApiData.title}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Endpoint Prefix: {currentApiData.prefix}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Chip 
              icon={<Zap size={16} />} 
              label="v1.0" 
              sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontWeight: "bold" }} 
            />
            <Chip 
              icon={<Shield size={16} />} 
              label="Secure" 
              sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }} 
            />
          </Stack>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xxl" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          {/* Sidebar */}
          <Grid item xs={12} md={3}>
            <Box sx={{ position: "sticky", top: 100 }}>
              
              {/* 🚀 API SWITCHER (New Section) 🚀 */}
              <Card sx={{ mb: 3 }}>
                <CardContent sx={{ pb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom fontWeight="bold">Select Documentation</Typography>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {Object.values(apiMap).map(api => (
                            <Chip
                                key={api.key}
                                // Normalize key (e.g., SIM_INVENTORY -> Sim Inventory) for display
                                label={api.key.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ')}
                                onClick={() => handleApiSwitch(api.key)}
                                color={activeApiKey === api.key ? "primary" : "default"}
                                variant={activeApiKey === api.key ? "filled" : "outlined"}
                                sx={{ fontWeight: activeApiKey === api.key ? 'bold' : 'normal', mb: 1 }}
                            />
                        ))}
                    </Stack>
                </CardContent>
              </Card>

              {/* Quick Stats (Dynamic) */}
              <Card sx={{ mb: 3, overflow: "visible" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                    <Activity size={18} style={{ marginRight: 8 }} />
                    API Overview
                  </Typography>
                  <Stack spacing={2}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2">Endpoints</Typography>
                      <Badge badgeContent={apiEndpoints.length} color="primary" />
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2">Avg Response</Typography>
                      <Typography variant="body2" color="success.main" fontWeight="bold">~150ms</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2">Uptime</Typography>
                      <Typography variant="body2" color="success.main" fontWeight="bold">99.9%</Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              {/* Navigation (Dynamic) */}
              <Card>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
                  <Typography variant="h6" sx={{ display: "flex", alignItems: "center" }}>
                    <Book size={18} style={{ marginRight: 8 }} />
                    Navigation
                  </Typography>
                </Box>
                <List dense>
                  {/* Static Links */}
                  <ListItemButton href="#introduction" sx={{ py: 1 }}><Globe size={16} style={{ marginRight: 12 }} /><ListItemText primary="Introduction" /></ListItemButton>
                  <ListItemButton href="#authentication" sx={{ py: 1 }}><Lock size={16} style={{ marginRight: 12 }} /><ListItemText primary="Headers" /></ListItemButton>
                  <ListItemButton href="#status-codes" sx={{ py: 1 }}><Server size={16} style={{ marginRight: 12 }} /><ListItemText primary="Status Codes" /></ListItemButton>
                  <ListItemButton href="#endpoints" sx={{ py: 1 }}><Code size={16} style={{ marginRight: 12 }} /><ListItemText primary="API Endpoints" /></ListItemButton>
                  
                  {/* Endpoint Links (Dynamic) */}
                  {apiEndpoints.map((ep) => (
                    <ListItemButton
                      key={ep.id}
                      href={`#endpoint-${ep.id}`}
                      sx={{ pl: 5, py:1 }}
                    >
                      <ListItemText 
                        primary={ep.title} 
                        primaryTypographyProps={{ fontSize: 14 }}
                      />
                      <Chip 
                        label={ep.method} 
                        size="small"
                        sx={{ 
                          minWidth: 45,
                          height: 20,
                          fontSize: 10,
                          bgcolor: methodConfig[ep.method].bg,
                          color: methodConfig[ep.method].color,
                          fontWeight: "bold"
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Card>
            </Box>
          </Grid>

          {/* Main Content */}
          <Grid item xs={12} md={9}>
            {/* Hero Introduction (Dynamic) */}
            <Card 
              id="introduction" 
              sx={{ 
                mb: 4, 
                background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                overflow: "visible"
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Stack direction="row" spacing={3} alignItems="center" mb={3}>
                  <Avatar sx={{ width: 60, height: 60, bgcolor: "#667eea" }}>
                    {currentApiData.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                      {/* Clean up the title from the icon character */}
                      {currentApiData.title.split(' 📘')[0].split(' ⚙️')[0]}
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      {currentApiData.description}
                    </Typography>
                  </Box>
                </Stack>

                <Typography paragraph sx={{ fontSize: 16, lineHeight: 1.7 }}>
                  This documentation provides complete details for the **{currentApiData.title.split(' API')[0]}**.
                </Typography>

                <Card variant="outlined" sx={{ bgcolor: "rgba(255,255,255,0.8)" }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                      <Globe size={16} style={{ marginRight: 8 }} />
                      Base URL
                    </Typography>
                    <Box sx={{ 
                      bgcolor: "#1a1a1a", color: "#4ade80", p: 2, borderRadius: 1, fontFamily: "monospace",
                      display: "flex", alignItems: "center", justifyContent: "space-between"
                    }}>
                      <code>{currentApiData.baseUrl}</code>
                      <IconButton 
                        size="small" 
                        onClick={() => copyToClipboard(currentApiData.baseUrl, "baseurl")}
                        sx={{ color: "#4ade80" }}
                      >
                        {copiedCode === "baseurl" ? <Check size={16} /> : <Copy size={16} />}
                      </IconButton>
                    </Box>
                    <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                        Endpoint Prefix: <code>{currentApiData.prefix}</code>
                    </Typography>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>

            {/* Headers/Authentication (Dynamic) */}
            <Card id="authentication" sx={{ mb: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                  <Shield size={24} style={{ marginRight: 12 }} />
                  Request Headers
                </Typography>
                <Typography paragraph color="text.secondary">
                  These headers are required for authentication or providing context across various endpoints in the **{currentApiData.title.split(' API')[0]}**.
                </Typography>

                <Grid container spacing={3}>
                  {requiredHeaders.map(header => (
                    <Grid item xs={12} md={6} key={header}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            {header}
                        </Typography>
                        <Box sx={{ bgcolor: "#1a1a1a", color: "#64b5f6", p: 2, borderRadius: 1, fontFamily: "monospace" }}>
                            {header}: [Value]
                        </Box>
                    </Grid>
                  ))}
                  {/* Always show Content-Type, as it's common */}
                  <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          Content-Type
                      </Typography>
                      <Box sx={{ bgcolor: "#1a1a1a", color: "#64b5f6", p: 2, borderRadius: 1, fontFamily: "monospace" }}>
                          Content-Type: application/json
                      </Box>
                      <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>
                            Required for requests sending a JSON body.
                      </Typography>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 3, p: 2, bgcolor: "#fff3cd", borderRadius: 1, border: "1px solid #ffeaa7" }}>
                  <Typography variant="body2">
                    💡 <strong>Note:</strong> Required headers depend on the active API and endpoint.
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Status Codes (Static) */}
            <Card id="status-codes" sx={{ mb: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
                  <Server size={24} style={{ marginRight: 12 }} />
                  HTTP Status Codes
                </Typography>
                <Typography paragraph color="text.secondary">
                  Our APIs use conventional HTTP response codes to indicate success or failure.
                </Typography>

                <Grid container spacing={2}>
                  {statusCodes.map((status) => (
                    <Grid item xs={12} md={6} key={status.code}>
                      <Card variant="outlined" sx={{ height: "100%" }}>
                        <CardContent sx={{ py: 2 }}>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Chip 
                              label={status.code}
                              sx={{ bgcolor: status.color, color: "white", fontWeight: "bold", minWidth: 50 }}
                            />
                            <Typography variant="body2">{status.desc}</Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            {/* API Endpoints (Dynamic) */}
            <Typography
              id="endpoints"
              variant="h5"
              fontWeight="bold"
              gutterBottom
              sx={{ display: "flex", alignItems: "center", mb: 3 }}
            >
              <Code size={24} style={{ marginRight: 12 }} />
              {currentApiData.title.split(' API')[0]} Endpoints ({apiEndpoints.length})
            </Typography>

            {/* MAPPING THE DATA TO THE REUSABLE ApiSection COMPONENT */}
            {apiEndpoints.map((ep) => (
              <ApiSection 
                key={ep.id} 
                endpointData={ep} 
                activeEndpoint={activeEndpoint} 
                setActiveEndpoint={setActiveEndpoint} 
                copiedCode={copiedCode} 
                copyToClipboard={copyToClipboard} 
              />
            ))}

            {/* Footer Section (Dynamic) */}
            <Card sx={{ mt: 6, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}>
              <CardContent sx={{ p: 4, color: "white", textAlign: "center" }}>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Ready to manage your {currentApiData.title.split(' API')[0]}?
                </Typography>
                <Typography paragraph sx={{ opacity: 0.9, mb: 3 }}>
                  Start integrating with the {currentApiData.title.split(' API')[0]} API today to automate your operations.
                </Typography>
                <Stack direction="row" spacing={2} justifyContent="center">
                  <Button 
                    variant="contained" 
                    sx={{ bgcolor: "white", color: "#667eea", "&:hover": { bgcolor: "#f8fafc" }, fontWeight: "bold" }}
                    startIcon={<Rocket />}
                  >
                    Get Account ID
                  </Button>
                  <Button 
                    variant="outlined" 
                    sx={{ borderColor: "white", color: "white", "&:hover": { borderColor: "white", bgcolor: "rgba(255,255,255,0.1)" } }}
                    startIcon={<ExternalLink />}
                  >
                    View SDKs
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ApiDoc;